import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { GetAdminConfig, UpdateAdminConfig, SeedMedals } from '../../../../utilities/BL/Xtp';

export const XtpConfigModal = ({ isOpen, onClose, onConfigSaved }) => {
    const { showMessage } = useFloatMessage();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSeeding, setIsSeeding] = useState(false);
    const [activeTab, setActiveTab] = useState('campaign'); // 'campaign' | 'prizes' | 'design' | 'codes' | 'legal'

    const [formData, setFormData] = useState({
        campaignTitle: 'Medallas Tradiciones Perronas 2026',
        campaignSubtitle: 'Club Tijuana Xoloitzcuintles de Caliente',
        passCodePrefix: '#TPX26-',
        localStoragePassTtlDays: 7,
        requireWhitelistMedalValidation: true,
        itemNoun: 'Xolo Folio',
        medalCodePrefix: 'XF-',
        medalRangeStart: 1,
        medalRangeEnd: 333,
        medalCodePadDigits: 3,
        winnerSelectionMode: 'RANDOM_COUNT',
        randomWinnersCount: 20,
        winningMedalCodes: 'XF-003, XF-033, XF-133, XF-233, XF-333',
        winningPrizeTitle: '¡Ganador de Boleto para Partido de Xolos Femenil!',
        winningPrizeDescription: 'Presenta este pase oficial digital o tu folio en taquilla o accesos oficiales para ingresar al partido.',
        winningPrizesJson: '',
        prizeDistributionMode: 'SEQUENTIAL',
        nonWinningTitle: '¡Ticket Registrado Exitosamente!',
        nonWinningMessage: 'Tu ticket de compra ha sido registrado y autenticado oficialmente por Club Tijuana Xoloitzcuintles.',
        nonWinnersDisplayMode: 'SHOW_MESSAGE_ONLY',
        ticketRequiredLength: '',
        showGiftBoxAnimation: true,
        showPassQr: true,
        showPassUserData: true,
        showPassLocationNote: true,
        bannerImageMdUrl: '/img/page/xolos-five-bicolor.png',
        bannerImageLgUrl: '/img/page/backgrounds/background-womens-day-2026-bold.png',
        isCampaignActive: true,
        termsAndConditionsText: '',
        privacyNoticeText: '',
        benefitsDescription: '',
        maxFailedAttemptsBeforeBlock: 5,
        blockDurationMinutes: 15
    });

    const [prizesList, setPrizesList] = useState([
        { id: 'prize-1', name: 'Boleto Xolos Femenil - 23 Oct', quantity: 10, description: 'Presenta tu pase oficial digital o folio en taquilla o acceso para ingresar al partido del 23 de Octubre en Estadio Caliente.' },
        { id: 'prize-2', name: 'Boleto Xolos Femenil - 31 Oct', quantity: 10, description: 'Presenta tu pase oficial digital o folio en taquilla o acceso para ingresar al partido del 31 de Octubre en Estadio Caliente.' }
    ]);

    useEffect(() => {
        if (!isOpen) return;

        const loadConfig = async () => {
            setIsLoading(true);
            try {
                const res = await GetAdminConfig();
                if (res && res.success && res.data) {
                    setFormData(prev => ({
                        ...prev,
                        ...res.data,
                        itemNoun: res.data.itemNoun || 'Xolo Folio',
                        medalCodePrefix: res.data.medalCodePrefix || 'XF-',
                        bannerImageMdUrl: res.data.bannerImageMdUrl || '/img/page/xolos-five-bicolor.png',
                        bannerImageLgUrl: res.data.bannerImageLgUrl || '/img/page/backgrounds/background-womens-day-2026-bold.png',
                        ticketRequiredLength: res.data.ticketRequiredLength !== null && res.data.ticketRequiredLength !== undefined ? String(res.data.ticketRequiredLength) : '',
                        randomWinnersCount: res.data.randomWinnersCount ?? 20,
                        winningPrizesJson: res.data.winningPrizesJson || '',
                        prizeDistributionMode: res.data.prizeDistributionMode || 'SEQUENTIAL'
                    }));

                    if (res.data.winningPrizesJson) {
                        try {
                            const parsed = JSON.parse(res.data.winningPrizesJson);
                            if (Array.isArray(parsed) && parsed.length > 0) {
                                setPrizesList(parsed);
                            }
                        } catch (parseErr) {
                            console.warn('Error al parsear winningPrizesJson:', parseErr);
                        }
                    }
                }
            } catch (err) {
                console.error('Error al cargar configuración:', err);
                showMessage({
                    message: 'No fue posible cargar la configuración actual.',
                    type: MESSAGE_TYPE.ERROR
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadConfig();
    }, [isOpen, showMessage]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddPrize = () => {
        setPrizesList(prev => [
            ...prev,
            {
                id: `prize-${Date.now()}`,
                name: '',
                quantity: 1,
                description: ''
            }
        ]);
    };

    const handleUpdatePrize = (index, field, value) => {
        setPrizesList(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: field === 'quantity' ? (parseInt(value, 10) || 0) : value
            };
            return updated;
        });
    };

    const handleRemovePrize = (index) => {
        if (prizesList.length <= 1) return;
        setPrizesList(prev => prev.filter((_, i) => i !== index));
    };

    const totalCatalogPrizes = prizesList.reduce((acc, p) => acc + (parseInt(p.quantity, 10) || 0), 0);

    const handleSeedMedals = async () => {
        const noun = formData.itemNoun || 'artículos';
        const startNum = String(formData.medalRangeStart || 1).padStart(formData.medalCodePadDigits || 3, '0');
        const endNum = String(formData.medalRangeEnd || 333).padStart(formData.medalCodePadDigits || 3, '0');
        const prefix = formData.medalCodePrefix || 'XF-';

        const totalPrizes = prizesList.reduce((acc, p) => acc + (parseInt(p.quantity, 10) || 0), 0);
        const winnersDetail = formData.winnerSelectionMode === 'RANDOM_COUNT'
            ? `${totalPrizes} ganadores al azar con premios del catálogo (${formData.prizeDistributionMode === 'SEQUENTIAL' ? 'Secuencial' : formData.prizeDistributionMode === 'RANDOM' ? 'Aleatorio' : 'Intercalado'})`
            : 'los códigos ganadores configurados';

        if (!window.confirm(`¿Deseas sincronizar o generar en base de datos el lote oficial de ${noun} (${prefix}${startNum} al ${prefix}${endNum}) marcando ${winnersDetail}?`)) {
            return;
        }

        setIsSeeding(true);
        try {
            const rawWinners = formData.winningMedalCodes
                ? formData.winningMedalCodes.split(/[,;| ]+/).filter(Boolean)
                : [];

            const effectiveRandomCount = formData.winnerSelectionMode === 'RANDOM_COUNT' && totalPrizes > 0
                ? totalPrizes
                : (parseInt(formData.randomWinnersCount) || 5);

            const res = await SeedMedals({
                prefix: prefix,
                start: parseInt(formData.medalRangeStart) || 1,
                end: parseInt(formData.medalRangeEnd) || 333,
                padDigits: parseInt(formData.medalCodePadDigits) || 3,
                batchName: `LOTE-${prefix.replace(/[^A-Za-z0-9]/g, '')}-OFICIAL`,
                winnerSelectionMode: formData.winnerSelectionMode,
                randomWinnersCount: effectiveRandomCount,
                winnerCodes: rawWinners,
                winningPrizesJson: JSON.stringify(prizesList),
                prizeDistributionMode: formData.prizeDistributionMode || 'SEQUENTIAL'
            });

            if (res && res.success) {
                showMessage({
                    message: `¡Éxito! ${res.message || 'Lote de códigos generado y sincronizado.'}`,
                    type: MESSAGE_TYPE.SUCCESS
                });
            } else {
                showMessage({
                    message: res?.message || 'Error al sincronizar el lote de folios.',
                    type: MESSAGE_TYPE.ERROR
                });
            }
        } catch (err) {
            console.error('Error al precargar lote:', err);
            showMessage({
                message: 'Error de conexión al sincronizar el lote.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setIsSeeding(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        const totalPrizes = prizesList.reduce((acc, p) => acc + (parseInt(p.quantity, 10) || 0), 0);
        const payload = {
            ...formData,
            ticketRequiredLength: formData.ticketRequiredLength !== '' ? parseInt(formData.ticketRequiredLength) : null,
            randomWinnersCount: formData.winnerSelectionMode === 'RANDOM_COUNT' && totalPrizes > 0
                ? totalPrizes
                : (formData.randomWinnersCount !== '' ? parseInt(formData.randomWinnersCount) : 5),
            medalRangeStart: parseInt(formData.medalRangeStart) || 1,
            medalRangeEnd: parseInt(formData.medalRangeEnd) || 333,
            medalCodePadDigits: parseInt(formData.medalCodePadDigits) || 3,
            winningPrizesJson: JSON.stringify(prizesList),
            prizeDistributionMode: formData.prizeDistributionMode || 'SEQUENTIAL'
        };

        try {
            const res = await UpdateAdminConfig(payload);
            if (res && res.success) {
                showMessage({
                    message: '¡Configuración guardada e invalidada en caché exitosamente!',
                    type: MESSAGE_TYPE.SUCCESS
                });
                if (onConfigSaved) onConfigSaved(res.data);
                onClose();
            } else {
                showMessage({
                    message: res?.message || 'Error al guardar la configuración.',
                    type: MESSAGE_TYPE.ERROR
                });
            }
        } catch (err) {
            console.error('Error al actualizar configuración:', err);
            showMessage({
                message: 'Error al conectar con el servidor para actualizar la configuración.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const sampleStart = String(formData.medalRangeStart || 1).padStart(formData.medalCodePadDigits || 3, '0');
    const sampleEnd = String(formData.medalRangeEnd || 333).padStart(formData.medalCodePadDigits || 3, '0');

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1060 }} tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                    {/* Header Principal */}
                    <div className="modal-header bg-dark text-white px-4 py-3 border-bottom border-danger">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-sliders text-danger fs-4"></i>
                            <div>
                                <h5 className="modal-title fw-bold text-white mb-0">
                                    Personalización y Configuración
                                </h5>
                                <div className="text-secondary small">
                                    {formData.campaignTitle} &bull; Dinámica Phygital
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            disabled={isSaving || isSeeding}
                        ></button>
                    </div>

                    {/* Navegación por Pestañas Amigables */}
                    <ul className="nav nav-pills nav-fill bg-dark px-3 py-2 border-bottom border-secondary border-opacity-25 gap-2">
                        <li className="nav-item">
                            <button
                                type="button"
                                className={`nav-link text-white fw-bold py-2 px-3 rounded-3 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'campaign' ? 'active bg-danger shadow-sm' : 'bg-transparent text-secondary'}`}
                                onClick={() => setActiveTab('campaign')}
                            >
                                <i className="bi bi-megaphone-fill"></i>
                                <span>1. Campaña</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                type="button"
                                className={`nav-link text-white fw-bold py-2 px-3 rounded-3 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'prizes' ? 'active bg-danger shadow-sm' : 'bg-transparent text-secondary'}`}
                                onClick={() => setActiveTab('prizes')}
                            >
                                <i className="bi bi-trophy-fill"></i>
                                <span>2. Ganadores y Premios</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                type="button"
                                className={`nav-link text-white fw-bold py-2 px-3 rounded-3 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'design' ? 'active bg-danger shadow-sm' : 'bg-transparent text-secondary'}`}
                                onClick={() => setActiveTab('design')}
                            >
                                <i className="bi bi-palette-fill"></i>
                                <span>3. Imagen y Fondo</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                type="button"
                                className={`nav-link text-white fw-bold py-2 px-3 rounded-3 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'codes' ? 'active bg-danger shadow-sm' : 'bg-transparent text-secondary'}`}
                                onClick={() => setActiveTab('codes')}
                            >
                                <i className="bi bi-upc-scan"></i>
                                <span>4. Códigos y Ticket</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                type="button"
                                className={`nav-link text-white fw-bold py-2 px-3 rounded-3 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'legal' ? 'active bg-danger shadow-sm' : 'bg-transparent text-secondary'}`}
                                onClick={() => setActiveTab('legal')}
                            >
                                <i className="bi bi-shield-check"></i>
                                <span>5. Textos Legales</span>
                            </button>
                        </li>
                    </ul>

                    {/* Cuerpo del Formulario */}
                    <form onSubmit={handleSave} className="modal-body p-4 bg-light">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-danger" role="status"></div>
                                <p className="text-secondary small mt-2">Cargando parámetros...</p>
                            </div>
                        ) : (
                            <>
                                {/* ========================================================
                                 * TAB 1: CAMPAÑA (TÍTULOS, NOMBRE DEL OBJETO, SWITCHES)
                                 * ======================================================== */}
                                {activeTab === 'campaign' && (
                                    <div className="row g-4 animate__animated animate__fadeIn">
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-info-circle-fill text-danger"></i>
                                                    Identidad General de la Campaña
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    Personaliza los títulos que verán los aficionados en la portada y el nombre oficial de su artículo físico.
                                                </p>

                                                <div className="row g-3">
                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">
                                                            Título Principal de la Dinámica <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={formData.campaignTitle}
                                                            onChange={(e) => handleInputChange('campaignTitle', e.target.value)}
                                                            placeholder="Ej. Medallas Tradiciones Perronas 2026"
                                                            required
                                                        />
                                                        <div className="form-text small">Se muestra en la parte superior de la página y en el pase digital.</div>
                                                    </div>

                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">
                                                            Subtítulo Institucional
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={formData.campaignSubtitle}
                                                            onChange={(e) => handleInputChange('campaignSubtitle', e.target.value)}
                                                            placeholder="Ej. Club Tijuana Xoloitzcuintles de Caliente"
                                                        />
                                                        <div className="form-text small">Nombre del club, patrocinador o división que respalda la dinámica.</div>
                                                    </div>

                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">
                                                            Nombre del Elemento / Artículo <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control fw-bold text-danger"
                                                            value={formData.itemNoun}
                                                            onChange={(e) => handleInputChange('itemNoun', e.target.value)}
                                                            placeholder="Ej. Xolo Folio, Medalla, Boleto, Pin"
                                                            required
                                                        />
                                                        <div className="form-text small">
                                                            ¿Cómo se llama lo que tienen en mano? (Ej. <strong>Xolo Folio</strong> o <strong>Medalla</strong>). Este término se adaptará automáticamente en botones y formularios.
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">
                                                            Estado de la Campaña
                                                        </label>
                                                        <div className="form-check form-switch p-2 bg-light rounded-3 border">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input ms-0 me-2"
                                                                id="switchActiveCampaign"
                                                                checked={formData.isCampaignActive}
                                                                onChange={(e) => handleInputChange('isCampaignActive', e.target.checked)}
                                                            />
                                                            <label className="form-check-label fw-semibold text-dark" htmlFor="switchActiveCampaign">
                                                                {formData.isCampaignActive ? '🟢 Campaña Activa al Público' : '🔴 Campaña Pausada'}
                                                            </label>
                                                        </div>
                                                        <div className="form-text small">Permite abrir o suspender temporalmente el registro a los aficionados.</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-gift-fill text-danger"></i>
                                                    Experiencia de Animación
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    Controla cómo se devela el resultado tras completar el formulario de registro.
                                                </p>

                                                <div className="form-check form-switch p-3 bg-light rounded-3 border">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input ms-0 me-2"
                                                        id="switchGiftBox"
                                                        checked={formData.showGiftBoxAnimation}
                                                        onChange={(e) => handleInputChange('showGiftBoxAnimation', e.target.checked)}
                                                    />
                                                    <label className="form-check-label fw-bold text-dark" htmlFor="switchGiftBox">
                                                        🎁 Mostrar Caja de Regalo Sorpresa Interactiva
                                                    </label>
                                                    <div className="text-muted small mt-1">
                                                        Si está <strong>activado</strong>, el aficionado verá una elegante caja de regalo roja con moño dorado que debe pulsar para destaparla con lluvia de confeti antes de ver su resultado. Si está <strong>desactivado</strong>, verá su resultado de forma directa e inmediata.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ========================================================
                                 * TAB 2: GANADORES Y PREMIOS
                                 * ======================================================== */}
                                {activeTab === 'prizes' && (
                                    <div className="row g-4 animate__animated animate__fadeIn">
                                        {/* Mecánica de Selección de Ganadores */}
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-trophy-fill text-warning"></i>
                                                    Mecánica de Selección de Ganadores
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    Define cómo se determina si un {formData.itemNoun || 'Xolo Folio'} tiene premio especial o es de registro regular.
                                                </p>

                                                <div className="row g-3">
                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">
                                                            Modo de Selección
                                                        </label>
                                                        <select
                                                            className="form-select fw-semibold"
                                                            value={formData.winnerSelectionMode}
                                                            onChange={(e) => handleInputChange('winnerSelectionMode', e.target.value)}
                                                        >
                                                            <option value="RANDOM_COUNT">🎲 Sorteo al Azar (Cuota de X Ganadores entre los {formData.medalRangeEnd || 333})</option>
                                                            <option value="SPECIFIC_CODES">🏷️ Códigos Específicos (Lista de Códigos Ganadores)</option>
                                                            <option value="ALL_WINNERS">🌟 Todos los Registros Ganan Premio</option>
                                                            <option value="NONE">🛡️ Sin Ganadores Especiales (Solo Colección)</option>
                                                        </select>
                                                        <div className="form-text small">Elige cómo premiar a los participantes.</div>
                                                    </div>

                                                    {formData.winnerSelectionMode === 'RANDOM_COUNT' ? (
                                                        <div className="col-12 col-md-6">
                                                            <label className="form-label fw-bold small text-dark">
                                                                ¿Cuántos Ganadores en Total? (Cuota X)
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                max={formData.medalRangeEnd || 1000}
                                                                className="form-control font-monospace fw-bold"
                                                                value={totalCatalogPrizes > 0 ? totalCatalogPrizes : (formData.randomWinnersCount ?? 20)}
                                                                onChange={(e) => handleInputChange('randomWinnersCount', e.target.value)}
                                                                placeholder="Ej. 20"
                                                                required
                                                            />
                                                            <div className="form-text small">
                                                                {totalCatalogPrizes > 0 ? (
                                                                    <span className="text-success fw-semibold">
                                                                        ✓ Sincronizado automáticamente con la suma del catálogo de premios ({totalCatalogPrizes} premios).
                                                                    </span>
                                                                ) : (
                                                                    'Cantidad aleatoria a sortear al sincronizar el lote.'
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="col-12 col-md-6">
                                                            <label className="form-label fw-bold small text-dark">
                                                                Códigos de {formData.itemNoun || 'Folios'} Ganadores
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control font-monospace fw-bold"
                                                                value={formData.winningMedalCodes || ''}
                                                                onChange={(e) => handleInputChange('winningMedalCodes', e.target.value)}
                                                                placeholder="XF-003, XF-033, XF-133, XF-233, XF-333"
                                                                disabled={formData.winnerSelectionMode !== 'SPECIFIC_CODES'}
                                                            />
                                                            <div className="form-text small">Separa los códigos por comas (ej. XF-003, XF-033, XF-133).</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Catálogo Dinámico de Premios / Boletos */}
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                                    <div>
                                                        <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                                            <i className="bi bi-ticket-perforated-fill text-danger"></i>
                                                            Catálogo Dinámico de Premios y Boletos
                                                        </h6>
                                                        <div className="text-muted small">
                                                            Configura los premios y sus cantidades (ej. 10 boletos de X partido y 10 de Y partido, o gorras, chelas, etc.).
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger fw-bold d-flex align-items-center gap-1 shadow-sm"
                                                        onClick={handleAddPrize}
                                                    >
                                                        <i className="bi bi-plus-circle-fill"></i>
                                                        ➕ Agregar Otro Premio
                                                    </button>
                                                </div>

                                                {/* Selector de Modo de Distribución */}
                                                <div className="p-3 bg-light rounded-3 border my-3">
                                                    <label className="form-label fw-bold small text-dark mb-2 d-flex align-items-center gap-2">
                                                        <i className="bi bi-shuffle text-danger"></i>
                                                        ¿Cómo se distribuirán los premios entre los ganadores?
                                                    </label>
                                                    <div className="row g-2">
                                                        <div className="col-12 col-md-4">
                                                            <div
                                                                className={`p-2 rounded-2 border cursor-pointer h-100 ${formData.prizeDistributionMode === 'SEQUENTIAL' ? 'bg-danger text-white border-danger shadow-sm' : 'bg-white text-dark'}`}
                                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                                onClick={() => handleInputChange('prizeDistributionMode', 'SEQUENTIAL')}
                                                            >
                                                                <div className="fw-bold small d-flex align-items-center gap-1">
                                                                    <span>1️⃣</span> En orden (Secuencial)
                                                                </div>
                                                                <div className="small mt-1 opacity-75" style={{ fontSize: '0.73rem' }}>
                                                                    Se dan primero los del Premio 1 (ej. los primeros 10 ganan Partido A), y luego los del Premio 2 (los siguientes 10 ganan Partido B).
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12 col-md-4">
                                                            <div
                                                                className={`p-2 rounded-2 border cursor-pointer h-100 ${formData.prizeDistributionMode === 'RANDOM' ? 'bg-danger text-white border-danger shadow-sm' : 'bg-white text-dark'}`}
                                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                                onClick={() => handleInputChange('prizeDistributionMode', 'RANDOM')}
                                                            >
                                                                <div className="fw-bold small d-flex align-items-center gap-1">
                                                                    <span>🔀</span> Al azar (Sorteo mixto)
                                                                </div>
                                                                <div className="small mt-1 opacity-75" style={{ fontSize: '0.73rem' }}>
                                                                    Se mezclan aleatoriamente todos los premios entre los folios ganadores.
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12 col-md-4">
                                                            <div
                                                                className={`p-2 rounded-2 border cursor-pointer h-100 ${formData.prizeDistributionMode === 'ALTERNATED' ? 'bg-danger text-white border-danger shadow-sm' : 'bg-white text-dark'}`}
                                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                                onClick={() => handleInputChange('prizeDistributionMode', 'ALTERNATED')}
                                                            >
                                                                <div className="fw-bold small d-flex align-items-center gap-1">
                                                                    <span>⚡</span> Uno y uno (Intercalado)
                                                                </div>
                                                                <div className="small mt-1 opacity-75" style={{ fontSize: '0.73rem' }}>
                                                                    Se entrega un premio de A, uno de B, uno de A... sucesivamente.
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Lista de Premios */}
                                                <div className="d-flex flex-column gap-3 mb-3">
                                                    {prizesList.map((prize, idx) => (
                                                        <div key={prize.id || idx} className="p-3 bg-light rounded-3 border position-relative">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span className="badge bg-danger text-white fw-bold">
                                                                    Premio #{idx + 1}
                                                                </span>
                                                                {prizesList.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-link text-danger p-0 text-decoration-none fw-bold small"
                                                                        onClick={() => handleRemovePrize(idx)}
                                                                        title="Eliminar este premio"
                                                                    >
                                                                        <i className="bi bi-trash-fill me-1"></i> Quitar
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="row g-2">
                                                                <div className="col-12 col-md-8">
                                                                    <label className="form-label small fw-bold text-dark mb-1">
                                                                        Nombre del Premio / Boleto <span className="text-danger">*</span>
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control form-control-sm fw-semibold"
                                                                        value={prize.name}
                                                                        onChange={(e) => handleUpdatePrize(idx, 'name', e.target.value)}
                                                                        placeholder="Ej. Boleto Xolos vs Necaxa - 25 Oct"
                                                                        required
                                                                    />
                                                                    <div className="form-text small" style={{ fontSize: '0.72rem' }}>
                                                                        Se mostrará destacado en la tarjeta dorada del ganador y en el escáner del staff.
                                                                    </div>
                                                                </div>
                                                                <div className="col-12 col-md-4">
                                                                    <label className="form-label small fw-bold text-dark mb-1">
                                                                        Cantidad de Premios <span className="text-danger">*</span>
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        max="500"
                                                                        className="form-control form-control-sm font-monospace fw-bold text-center"
                                                                        value={prize.quantity}
                                                                        onChange={(e) => handleUpdatePrize(idx, 'quantity', e.target.value)}
                                                                        required
                                                                    />
                                                                    <div className="form-text small" style={{ fontSize: '0.72rem' }}>
                                                                        Cuota de este premio
                                                                    </div>
                                                                </div>
                                                                <div className="col-12">
                                                                    <label className="form-label small fw-bold text-dark mb-1">
                                                                        Indicaciones específicas para reclamar (Opcional)
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control form-control-sm"
                                                                        value={prize.description || ''}
                                                                        onChange={(e) => handleUpdatePrize(idx, 'description', e.target.value)}
                                                                        placeholder="Ej. Presenta este pase en taquilla o acceso especial para ingresar al estadio."
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Resumen total de premios */}
                                                <div className="alert alert-warning py-2 px-3 small border-0 rounded-3 d-flex align-items-center justify-content-between flex-wrap gap-2 mb-0">
                                                    <div>
                                                        <i className="bi bi-gift-fill me-1 text-danger"></i>
                                                        Total acumulado de premios: <strong className="text-danger fs-6">{totalCatalogPrizes} premios</strong>
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                                        {formData.prizeDistributionMode === 'SEQUENTIAL' ? '1️⃣ En orden (Secuencial)' : formData.prizeDistributionMode === 'RANDOM' ? '🔀 Al azar (Sorteo mixto)' : '⚡ Uno y uno (Intercalado)'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Detalle del Premio Ganador (Textos de respaldo) */}
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-award-fill text-danger"></i>
                                                    Textos Generales de Respaldo para Ganadores
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    Si un premio del catálogo no cuenta con indicaciones particulares, se utilizarán estos textos por defecto.
                                                </p>

                                                <div className="row g-3">
                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">Título General del Premio Ganador</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={formData.winningPrizeTitle || ''}
                                                            onChange={(e) => handleInputChange('winningPrizeTitle', e.target.value)}
                                                            placeholder="Ej. ¡Xolo Folio Ganador de Premio Especial!"
                                                        />
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">Instrucción General para Reclamar</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={formData.winningPrizeDescription || ''}
                                                            onChange={(e) => handleInputChange('winningPrizeDescription', e.target.value)}
                                                            placeholder="Presenta tu pase con el staff en Xolo Shop o Estadio Caliente..."
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Experiencia para No Ganadores */}
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-people-fill text-primary"></i>
                                                    Experiencia para Participantes Regulares (No Ganadores)
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    Decide si quien no obtiene premio especial recibe una tarjeta digital o únicamente un mensaje de agradecimiento.
                                                </p>

                                                <div className="row g-3">
                                                    <div className="col-12 col-md-4">
                                                        <label className="form-label fw-bold small text-dark">¿Qué ven al terminar?</label>
                                                        <select
                                                            className="form-select fw-semibold"
                                                            value={formData.nonWinnersDisplayMode}
                                                            onChange={(e) => handleInputChange('nonWinnersDisplayMode', e.target.value)}
                                                        >
                                                            <option value="SHOW_MESSAGE_ONLY">💬 Mostrar Solo Mensaje (Estilo MVP Xolos)</option>
                                                            <option value="SHOW_DIGITAL_PASS">🎖️ Mostrar Pase Digital de Colección</option>
                                                        </select>
                                                        <div className="form-text small">
                                                            {formData.nonWinnersDisplayMode === 'SHOW_MESSAGE_ONLY'
                                                                ? 'Se muestra una pantalla elegante con su confirmación de registro sin tarjeta.'
                                                                : 'Se genera una tarjeta de aficionado oficial descargable.'}
                                                        </div>
                                                    </div>
                                                    <div className="col-12 col-md-4">
                                                        <label className="form-label fw-bold small text-dark">Título Regular</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={formData.nonWinningTitle || ''}
                                                            onChange={(e) => handleInputChange('nonWinningTitle', e.target.value)}
                                                            placeholder="Ej. ¡Xolo Folio Oficial Registrado!"
                                                        />
                                                    </div>
                                                    <div className="col-12 col-md-4">
                                                        <label className="form-label fw-bold small text-dark">Mensaje Regular</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={formData.nonWinningMessage || ''}
                                                            onChange={(e) => handleInputChange('nonWinningMessage', e.target.value)}
                                                            placeholder="Tu folio ha sido validado y autenticado..."
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ========================================================
                                 * TAB 3: DISEÑO Y FONDO (IMAGEN, VIDEO, LOGO)
                                 * ======================================================== */}
                                {activeTab === 'design' && (
                                    <div className="row g-4 animate__animated animate__fadeIn">
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-image text-danger"></i>
                                                    Logotipo Superior
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    El logotipo que encabeza la pantalla del aficionado y se proyecta en la tarjeta digital.
                                                </p>

                                                <div className="row g-3 align-items-center">
                                                    <div className="col-12 col-md-8">
                                                        <label className="form-label fw-bold small text-dark">URL del Logotipo</label>
                                                        <input
                                                            type="text"
                                                            className="form-control font-monospace small"
                                                            value={formData.bannerImageMdUrl || ''}
                                                            onChange={(e) => handleInputChange('bannerImageMdUrl', e.target.value)}
                                                            placeholder="/img/page/xolos-five-bicolor.png"
                                                        />
                                                        <div className="d-flex gap-2 mt-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => handleInputChange('bannerImageMdUrl', '/img/page/xolos-five-bicolor.png')}
                                                            >
                                                                <i className="bi bi-arrow-counterclockwise me-1"></i>
                                                                Logo Oficial Club Tijuana Bicolor
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 col-md-4 text-center">
                                                        <div className="p-2 bg-dark rounded-3 border d-inline-block shadow-sm" style={{ minWidth: '180px' }}>
                                                            <div className="text-white-50 small mb-1" style={{ fontSize: '0.72rem' }}>Vista previa del logo</div>
                                                            <img
                                                                src={formData.bannerImageMdUrl || '/img/page/xolos-five-bicolor.png'}
                                                                alt="Preview Logo"
                                                                style={{ maxHeight: '50px', maxWidth: '160px', objectFit: 'contain' }}
                                                                onError={(e) => { e.target.style.opacity = '0.3'; }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-camera-reels text-danger"></i>
                                                    Fondo de Pantalla (Imagen o Video)
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    Permite colocar una imagen (.jpg, .png, .webp) o un video (.mp4, .webm) que se reproducirá de fondo automáticamente.
                                                </p>

                                                <div className="row g-3">
                                                    <div className="col-12 col-md-8">
                                                        <label className="form-label fw-bold small text-dark">URL del Fondo de Pantalla</label>
                                                        <input
                                                            type="text"
                                                            className="form-control font-monospace small"
                                                            value={formData.bannerImageLgUrl || ''}
                                                            onChange={(e) => handleInputChange('bannerImageLgUrl', e.target.value)}
                                                            placeholder="/img/page/backgrounds/background-womens-day-2026-bold.png o https://...video.mp4"
                                                        />
                                                        <div className="form-text small">
                                                            Si escribes la dirección de un video <code>.mp4</code>, se reproducirá en bucle silencioso. Si es una imagen o ruta vacía, se usará el fondo oscuro de Club Tijuana.
                                                        </div>

                                                        <div className="d-flex flex-wrap gap-2 mt-3">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleInputChange('bannerImageLgUrl', '/img/page/backgrounds/background-womens-day-2026-bold.png')}
                                                            >
                                                                🔴 Fondo Oficial Xolos Bold (Estadio)
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => handleInputChange('bannerImageLgUrl', '')}
                                                            >
                                                                🌑 Gradiente Oscuro Puro (Sin Imagen)
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-4 text-center">
                                                        <div className="p-2 bg-dark rounded-3 border shadow-sm h-100 d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '130px' }}>
                                                            <div className="text-white-50 small mb-1" style={{ fontSize: '0.72rem' }}>Vista previa del fondo</div>
                                                            {formData.bannerImageLgUrl && (formData.bannerImageLgUrl.endsWith('.mp4') || formData.bannerImageLgUrl.endsWith('.webm')) ? (
                                                                <video
                                                                    src={formData.bannerImageLgUrl}
                                                                    autoPlay
                                                                    loop
                                                                    muted
                                                                    playsInline
                                                                    className="rounded-2 shadow-sm"
                                                                    style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'cover' }}
                                                                />
                                                            ) : formData.bannerImageLgUrl ? (
                                                                <img
                                                                    src={formData.bannerImageLgUrl}
                                                                    alt="Fondo Preview"
                                                                    className="rounded-2 shadow-sm"
                                                                    style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'cover' }}
                                                                    onError={(e) => { e.target.style.opacity = '0.3'; }}
                                                                />
                                                            ) : (
                                                                <div className="text-secondary small p-3">
                                                                    <i className="bi bi-circle-fill me-1 text-danger"></i> Gradiente Oscuro Oficial
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ========================================================
                                 * TAB 4: CÓDIGOS Y TICKET (RANGO, SEED, VALIDACIÓN TICKET)
                                 * ======================================================== */}
                                {activeTab === 'codes' && (
                                    <div className="row g-4 animate__animated animate__fadeIn">
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                                    <div>
                                                        <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                                            <i className="bi bi-upc-scan text-danger"></i>
                                                            Rango de Folios Físicos ({formData.itemNoun || 'Xolo Folios'})
                                                        </h6>
                                                        <div className="text-muted small">
                                                            Define el prefijo oficial grabado en el artículo y el rango numérico.
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger fw-bold d-flex align-items-center gap-1 shadow-sm"
                                                        onClick={handleSeedMedals}
                                                        disabled={isSeeding || isSaving}
                                                    >
                                                        {isSeeding ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                Sincronizando lote...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-cloud-arrow-up-fill"></i>
                                                                ⚡ Sincronizar Lote en Base de Datos
                                                            </>
                                                        )}
                                                    </button>
                                                </div>

                                                <div className="alert alert-info py-2 px-3 small border-0 rounded-3 mb-2">
                                                    <i className="bi bi-eye-fill me-1"></i>
                                                    Muestra del rango generado: <strong className="font-monospace text-dark">{formData.medalCodePrefix}{sampleStart}</strong> al <strong className="font-monospace text-dark">{formData.medalCodePrefix}{sampleEnd}</strong>.
                                                </div>

                                                <div className="alert alert-warning py-2 px-3 small border-0 rounded-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                                                    <div>
                                                        <i className="bi bi-ticket-detailed-fill me-1 text-danger"></i>
                                                        Premios a distribuir al sincronizar: <strong className="text-danger">{totalCatalogPrizes} premios</strong> en modo <strong>{formData.prizeDistributionMode === 'SEQUENTIAL' ? '1️⃣ Secuencial (en orden)' : formData.prizeDistributionMode === 'RANDOM' ? '🔀 Aleatorio (mixto)' : '⚡ Intercalado (uno y uno)'}</strong>.
                                                    </div>
                                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        Configurados en Pestaña 2
                                                    </span>
                                                </div>

                                                <div className="row g-3">
                                                    <div className="col-12 col-md-3">
                                                        <label className="form-label fw-bold small text-dark">Prefijo de Código</label>
                                                        <input
                                                            type="text"
                                                            className="form-control font-monospace fw-bold"
                                                            value={formData.medalCodePrefix}
                                                            onChange={(e) => handleInputChange('medalCodePrefix', e.target.value.toUpperCase())}
                                                            placeholder="Ej. XF-"
                                                            required
                                                        />
                                                        <div className="form-text small">Grabado en el artículo</div>
                                                    </div>
                                                    <div className="col-12 col-md-3">
                                                        <label className="form-label fw-bold small text-dark">Número Inicial</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className="form-control"
                                                            value={formData.medalRangeStart}
                                                            onChange={(e) => handleInputChange('medalRangeStart', e.target.value)}
                                                            required
                                                        />
                                                        <div className="form-text small">Ej. 1</div>
                                                    </div>
                                                    <div className="col-12 col-md-3">
                                                        <label className="form-label fw-bold small text-dark">Número Final</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className="form-control"
                                                            value={formData.medalRangeEnd}
                                                            onChange={(e) => handleInputChange('medalRangeEnd', e.target.value)}
                                                            required
                                                        />
                                                        <div className="form-text small">Ej. 333</div>
                                                    </div>
                                                    <div className="col-12 col-md-3">
                                                        <label className="form-label fw-bold small text-dark">Dígitos con Ceros (Padding)</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max="6"
                                                            className="form-control"
                                                            value={formData.medalCodePadDigits}
                                                            onChange={(e) => handleInputChange('medalCodePadDigits', e.target.value)}
                                                            required
                                                        />
                                                        <div className="form-text small">3 = 001..333</div>
                                                    </div>
                                                </div>

                                                <div className="mt-3">
                                                    <div className="form-check form-switch p-3 bg-light rounded-3 border">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input ms-0 me-2"
                                                            id="switchWhitelist"
                                                            checked={formData.requireWhitelistMedalValidation}
                                                            onChange={(e) => handleInputChange('requireWhitelistMedalValidation', e.target.checked)}
                                                        />
                                                        <label className="form-check-label fw-bold text-dark" htmlFor="switchWhitelist">
                                                            🛡️ Validar contra Lista Blanca Oficial de Códigos
                                                        </label>
                                                        <div className="text-muted small mt-1">
                                                            Si está activo, el aficionado <strong>solo</strong> podrá registrar folios que existan en la base de datos sincronizada y que no hayan sido registrados previamente.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Validación del Ticket de Compra */}
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-ticket-perforated-fill text-danger"></i>
                                                    Reglas de Validación del Ticket de Compra
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    Evita que los aficionados escriban números incompletos o erróneos al registrar su ticket físico.
                                                </p>

                                                <div className="row g-3">
                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">
                                                            Longitud Fija de Caracteres del Ticket
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max="50"
                                                            className="form-control font-monospace"
                                                            value={formData.ticketRequiredLength}
                                                            onChange={(e) => handleInputChange('ticketRequiredLength', e.target.value)}
                                                            placeholder="Ej. 12 (deja vacío para formato libre)"
                                                        />
                                                        <div className="form-text small">
                                                            Si tus tickets de caja tienen una cantidad exacta de caracteres (ej. <strong>12</strong> para boletos como <code>XTEC_1_27121</code>), indícalo aquí. Si no requieres longitud fija, déjalo en blanco.
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-6">
                                                        <label className="form-label fw-bold small text-dark">
                                                            Prefijo de Folio del Pase Digital
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control font-monospace"
                                                            value={formData.passCodePrefix}
                                                            onChange={(e) => handleInputChange('passCodePrefix', e.target.value)}
                                                            placeholder="Ej. #TPX26-"
                                                            required
                                                        />
                                                        <div className="form-text small">
                                                            Prefijo con el que se emite el folio al aficionado (ej. <code>#TPX26-ABC12</code>).
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tarjeta Digital y Ciberseguridad */}
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-shield-lock-fill text-danger"></i>
                                                    Tarjeta Digital y Seguridad Defensiva
                                                </h6>
                                                <p className="text-muted small mb-3">
                                                    Controla la visibilidad de elementos en el pase y la protección contra ataques de enumeración.
                                                </p>

                                                <div className="row g-3">
                                                    <div className="col-12 col-md-4">
                                                        <div className="form-check form-switch p-3 bg-light rounded-3 border h-100">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input ms-0 me-2"
                                                                id="switchShowQr"
                                                                checked={formData.showPassQr}
                                                                onChange={(e) => handleInputChange('showPassQr', e.target.checked)}
                                                            />
                                                            <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="switchShowQr">
                                                                📱 Mostrar Código QR en la Tarjeta
                                                            </label>
                                                            <div className="text-muted small mt-1">
                                                                Muestra el QR criptográfico para escanear y validar en estadio o tienda física.
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-4">
                                                        <div className="form-check form-switch p-3 bg-light rounded-3 border h-100">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input ms-0 me-2"
                                                                id="switchShowUserData"
                                                                checked={formData.showPassUserData}
                                                                onChange={(e) => handleInputChange('showPassUserData', e.target.checked)}
                                                            />
                                                            <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="switchShowUserData">
                                                                👤 Mostrar Datos del Aficionado
                                                            </label>
                                                            <div className="text-muted small mt-1">
                                                                Muestra nombre, datos de contacto, folio y ticket en el diseño de la tarjeta.
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-4">
                                                        <div className="form-check form-switch p-3 bg-light rounded-3 border h-100">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input ms-0 me-2"
                                                                id="switchShowLocationNote"
                                                                checked={formData.showPassLocationNote}
                                                                onChange={(e) => handleInputChange('showPassLocationNote', e.target.checked)}
                                                            />
                                                            <label className="form-check-label fw-bold text-dark cursor-pointer" htmlFor="switchShowLocationNote">
                                                                📋 Mostrar Mensaje / Indicaciones
                                                            </label>
                                                            <div className="text-muted small mt-1">
                                                                Muestra u oculta la caja inferior de canje ("Muestra este pase o una captura...").
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-4">
                                                        <label className="form-label fw-bold small text-dark">Días Guardado en Teléfono (TTL)</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max="90"
                                                            className="form-control"
                                                            value={formData.localStoragePassTtlDays}
                                                            onChange={(e) => handleInputChange('localStoragePassTtlDays', parseInt(e.target.value) || 7)}
                                                            required
                                                        />
                                                        <div className="form-text small">Días que se conserva en el navegador del aficionado.</div>
                                                    </div>

                                                    <div className="col-12 col-md-4">
                                                        <label className="form-label fw-bold small text-dark">Intentos Fallidos antes de Pausa</label>
                                                        <input
                                                            type="number"
                                                            min="2"
                                                            max="20"
                                                            className="form-control"
                                                            value={formData.maxFailedAttemptsBeforeBlock}
                                                            onChange={(e) => handleInputChange('maxFailedAttemptsBeforeBlock', parseInt(e.target.value) || 5)}
                                                        />
                                                        <div className="form-text small">Umbral para evitar intentos automatizados.</div>
                                                    </div>

                                                    <div className="col-12 col-md-4">
                                                        <label className="form-label fw-bold small text-dark">Minutos de Pausa Temporal</label>
                                                        <input
                                                            type="number"
                                                            min="5"
                                                            max="120"
                                                            className="form-control"
                                                            value={formData.blockDurationMinutes}
                                                            onChange={(e) => handleInputChange('blockDurationMinutes', parseInt(e.target.value) || 15)}
                                                        />
                                                        <div className="form-text small">Tiempo de espera si se excede el umbral.</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ========================================================
                                 * TAB 5: TEXTOS LEGALES
                                 * ======================================================== */}
                                {activeTab === 'legal' && (
                                    <div className="row g-4 animate__animated animate__fadeIn">
                                        <div className="col-12">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-card-text text-danger"></i>
                                                    Descripción de Beneficios Oficiales
                                                </h6>
                                                <p className="text-muted small mb-2">
                                                    Texto breve explicativo sobre las ventajas de registrar el artículo oficial.
                                                </p>
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    value={formData.benefitsDescription || ''}
                                                    onChange={(e) => handleInputChange('benefitsDescription', e.target.value)}
                                                    placeholder="Acceso a beneficios exclusivos en tienda oficial y dinámicas especiales del Club Tijuana..."
                                                ></textarea>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm h-100 d-flex flex-column">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-file-earmark-ruled-fill text-danger"></i>
                                                    Términos y Condiciones
                                                </h6>
                                                <p className="text-muted small mb-2">
                                                    Reglamento legal que acepta el aficionado al registrarse.
                                                </p>
                                                <textarea
                                                    className="form-control font-monospace small flex-grow-1"
                                                    rows="6"
                                                    value={formData.termsAndConditionsText || ''}
                                                    onChange={(e) => handleInputChange('termsAndConditionsText', e.target.value)}
                                                    placeholder="Si se deja vacío, el sistema utilizará los Términos y Condiciones oficiales por defecto del Club Tijuana."
                                                ></textarea>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <div className="p-3 bg-white rounded-3 border shadow-sm h-100 d-flex flex-column">
                                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                                    <i className="bi bi-shield-check text-danger"></i>
                                                    Aviso de Privacidad
                                                </h6>
                                                <p className="text-muted small mb-2">
                                                    Política de tratamiento de datos personales de Club Tijuana.
                                                </p>
                                                <textarea
                                                    className="form-control font-monospace small flex-grow-1"
                                                    rows="6"
                                                    value={formData.privacyNoticeText || ''}
                                                    onChange={(e) => handleInputChange('privacyNoticeText', e.target.value)}
                                                    placeholder="Si se deja vacío, el sistema utilizará el Aviso de Privacidad simplificado oficial por defecto."
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Footer del Modal */}
                        <div className="modal-footer px-0 pb-0 pt-3 border-top mt-3 d-flex justify-content-between align-items-center">
                            <button
                                type="button"
                                className="btn btn-secondary px-4 fw-semibold"
                                onClick={onClose}
                                disabled={isSaving || isSeeding}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-danger px-4 fw-bold d-flex align-items-center gap-2 shadow-sm"
                                disabled={isSaving || isLoading || isSeeding}
                            >
                                {isSaving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                        Guardando cambios...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill"></i>
                                        Guardar Parámetros
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
